/**
 * AppVersionGuard — detección y aplicación segura de nuevas versiones.
 * @module utils/appVersionGuard
 */

import {
  APP_VERSION_SNAPSHOT_KEY,
  PENDING_APP_VERSION_KEY,
  purgeCleanableLocalStorage
} from './versionStoragePolicy';
import { purgeKnownInvalidSucursalSelection } from './sucursalStorageValidation';
import { hasCriticalOperationActive, isPosProtectedRoute } from './criticalOperationGuard';

const VERSION_URL = '/version.json';
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 15 * 1000;
const POSTPONE_DURATION_MS = 5 * 60 * 1000;
export const VERSION_UPDATE_POSTPONED_UNTIL_KEY = 'app_version_postponed_until';
export const VERSION_RELOAD_MARKER_KEY = 'nexo_intentional_version_reload_at';
export const VERSION_RELOAD_GRACE_MS = 15000;

let applyingUpdate = false;
let lastCheckAt = 0;

export function readLocalVersionSnapshot() {
  try {
    const raw = localStorage.getItem(APP_VERSION_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeLocalVersionSnapshot(snapshot) {
  try {
    localStorage.setItem(APP_VERSION_SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

export async function fetchServerVersion() {
  const response = await fetch(VERSION_URL, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' }
  });
  if (!response.ok) {
    throw new Error(`version.json HTTP ${response.status}`);
  }
  return response.json();
}

export function isUpdateAvailable(localSnapshot, remoteSnapshot) {
  if (!remoteSnapshot?.commit) return false;
  if (!localSnapshot) return false;
  return (
    localSnapshot.commit !== remoteSnapshot.commit
    || localSnapshot.version !== remoteSnapshot.version
  );
}

export function readPendingVersion() {
  try {
    const raw = sessionStorage.getItem(PENDING_APP_VERSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function writePendingVersion(snapshot) {
  try {
    sessionStorage.setItem(PENDING_APP_VERSION_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

export function clearPendingVersion() {
  try {
    sessionStorage.removeItem(PENDING_APP_VERSION_KEY);
  } catch {
    // ignore
  }
}

export function readPostponedUntil() {
  try {
    const raw = sessionStorage.getItem(VERSION_UPDATE_POSTPONED_UNTIL_KEY);
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function postponeVersionUpdate(durationMs = POSTPONE_DURATION_MS) {
  try {
    sessionStorage.setItem(
      VERSION_UPDATE_POSTPONED_UNTIL_KEY,
      String(Date.now() + durationMs)
    );
  } catch {
    // ignore
  }
}

export function clearVersionUpdatePostpone() {
  try {
    sessionStorage.removeItem(VERSION_UPDATE_POSTPONED_UNTIL_KEY);
  } catch {
    // ignore
  }
}

export function isVersionUpdatePostponed() {
  return readPostponedUntil() > Date.now();
}

export function markIntentionalVersionReload() {
  try {
    sessionStorage.setItem(VERSION_RELOAD_MARKER_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function isWithinVersionReloadGrace() {
  try {
    const raw = sessionStorage.getItem(VERSION_RELOAD_MARKER_KEY);
    const at = Number(raw);
    if (!Number.isFinite(at) || at <= 0) return false;
    if (Date.now() - at > VERSION_RELOAD_GRACE_MS) {
      sessionStorage.removeItem(VERSION_RELOAD_MARKER_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function clearVersionReloadMarker() {
  try {
    sessionStorage.removeItem(VERSION_RELOAD_MARKER_KEY);
  } catch {
    // ignore
  }
}

export function getPostponeDurationMs() {
  return POSTPONE_DURATION_MS;
}

export async function clearBrowserCaches() {
  try {
    if (typeof window !== 'undefined' && window.caches?.keys) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch {
    // ignore
  }
}

export async function registerLegacyServiceWorkerCleanup() {
  try {
    if (typeof navigator === 'undefined' || !navigator.serviceWorker?.register) return;
    await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    // ignore — SW legacy es best-effort
  }
}

export function performSafeStorageCleanup() {
  purgeKnownInvalidSucursalSelection();
  return purgeCleanableLocalStorage();
}

/**
 * @returns {Promise<{ status: 'noop'|'initialized'|'deferred'|'applied'|'update_available'|'postponed', remote?: object, local?: object }>}
 */
export async function evaluateAppVersion({ forceApply = false, pathname } = {}) {
  if (applyingUpdate) return { status: 'noop' };

  const resolvedPath = pathname ?? (typeof window !== 'undefined' ? window.location?.pathname : '');

  const now = Date.now();
  if (!forceApply && now - lastCheckAt < MIN_CHECK_GAP_MS) {
    return { status: 'noop' };
  }
  lastCheckAt = now;

  const remote = await fetchServerVersion();
  const local = readLocalVersionSnapshot();
  const controlledPanelUpdate = !isPosProtectedRoute(resolvedPath);

  if (!local) {
    writeLocalVersionSnapshot(remote);
    purgeKnownInvalidSucursalSelection();
    return { status: 'initialized', remote, local: null };
  }

  const pending = readPendingVersion();
  const updateReady = pending
    ? pending.commit !== local.commit || pending.version !== local.version
    : isUpdateAvailable(local, remote);

  if (!updateReady && !forceApply) {
    return { status: 'noop', remote, local };
  }

  const targetVersion = pending || remote;

  if (!forceApply && hasCriticalOperationActive()) {
    writePendingVersion(targetVersion);
    return { status: 'deferred', remote: targetVersion, local };
  }

  if (!forceApply && controlledPanelUpdate) {
    if (isVersionUpdatePostponed()) {
      return { status: 'postponed', remote: targetVersion, local };
    }
    writePendingVersion(targetVersion);
    return { status: 'update_available', remote: targetVersion, local };
  }

  applyingUpdate = true;
  performSafeStorageCleanup();
  writeLocalVersionSnapshot(targetVersion);
  clearPendingVersion();
  clearVersionUpdatePostpone();
  await clearBrowserCaches();
  return { status: 'applied', remote: targetVersion, local };
}

export async function applyVersionUpdateNow(targetVersion) {
  if (!targetVersion?.commit) return { status: 'noop' };
  if (hasCriticalOperationActive()) {
    writePendingVersion(targetVersion);
    return { status: 'deferred', remote: targetVersion };
  }
  applyingUpdate = true;
  performSafeStorageCleanup();
  writeLocalVersionSnapshot(targetVersion);
  clearPendingVersion();
  clearVersionUpdatePostpone();
  markIntentionalVersionReload();
  await clearBrowserCaches();
  return { status: 'applied', remote: targetVersion };
}

export async function applyPendingVersionIfSafe(pathname) {
  const pending = readPendingVersion();
  if (!pending) return { status: 'noop' };
  const resolvedPath = pathname ?? (typeof window !== 'undefined' ? window.location?.pathname : '');
  if (!isPosProtectedRoute(resolvedPath)) {
    return { status: 'noop', remote: pending };
  }
  if (hasCriticalOperationActive()) return { status: 'deferred', remote: pending };
  applyingUpdate = true;
  performSafeStorageCleanup();
  writeLocalVersionSnapshot(pending);
  clearPendingVersion();
  markIntentionalVersionReload();
  await clearBrowserCaches();
  window.location.reload();
  return { status: 'applied', remote: pending };
}

export function getVersionCheckIntervalMs() {
  return CHECK_INTERVAL_MS;
}
